import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
/**
 * Sklep — /sklep
 * Pełna lista wszystkich produktów Shopify z wyszukiwaniem i sortowaniem.
 */
import { useState, useEffect, type MouseEvent } from 'react';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { Link } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, SlidersHorizontal, ChevronRight, ChevronDown, Loader2 } from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { ProductCard } from '@/components/ui/ProductCard';
import { productUnavailableReason } from '@/lib/productLabels';
import {
  fetchAllCollections, fetchAllProducts, searchProducts, productDisplayPrice, parseShortDescription,
  type ShopifyCollectionSummary, type ShopifyProduct,
} from '@/lib/shopifyApi';

import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { isAddonProduct, collectionUrl } from '@workspace/url-source';

/* ── tokens ─────────────────────────────────────────────── */
const GOLD  = '#e05590';
const DARK  = '#2a1f14';
const MUTED = 'rgba(42,31,20,0.50)';
const LINE  = 'rgba(210,60,120,0.18)';
const CREAM = '#faf7f2';
const CREAM2 = '#f5efe6';

type SortKey = 'default' | 'price-asc' | 'price-desc' | 'name-asc';
const SORT_LABELS: Record<SortKey, string> = {
  'default':    'Domyślne',
  'price-asc':  'Cena: od najniższej',
  'price-desc': 'Cena: od najwyższej',
  'name-asc':   'Nazwa: A–Z',
};

function toCardProps(p: ShopifyProduct, i: number) {
  return {
    labels: p.labels,
    purchaseBlocked: p.purchaseBlocked,
    unavailableMessage: p.unavailableMessage,
    image:             p.featuredImage?.url ?? null,
    name:              p.title,
    handle:            p.handle || undefined,
    price:             productDisplayPrice(p.priceRange),
    variants:          p.variants.nodes.map(v => ({ id: v.id, title: v.title === 'Default Title' ? p.title : v.title, availableForSale: v.availableForSale })),
    availability:      p.variants.nodes.some(v => v.availableForSale) ? 'Dostępny' : 'Brak',
    shortDescription:  parseShortDescription(p.shortDescription?.value),
    index:             i,
  };
}

function sortProducts(list: ShopifyProduct[], key: SortKey): ShopifyProduct[] {
  const copy = [...list];
  if (key === 'price-asc')  copy.sort((a, b) => parseFloat(a.priceRange.minVariantPrice.amount) - parseFloat(b.priceRange.minVariantPrice.amount));
  if (key === 'price-desc') copy.sort((a, b) => parseFloat(b.priceRange.minVariantPrice.amount) - parseFloat(a.priceRange.minVariantPrice.amount));
  if (key === 'name-asc')   copy.sort((a, b) => a.title.localeCompare(b.title, 'pl'));
  return copy;
}

const PAGE = 12;
const searchShopProducts = (query: string) => searchProducts(query, 50);

export default function SklepPage() {
  useSeo(PAGE_SEO.sklep);
  const [allProducts, setAllProducts]     = useState<ShopifyProduct[]>([]);
  const [collections, setCollections]     = useState<ShopifyCollectionSummary[]>([]);
  const [loading, setLoading]             = useState(true);
  usePrerenderReady(!loading);
  const [query, setQuery]                 = useState('');
  const { results: displayed, loading: searchLoading, error: searchError } = useDebouncedSearch(query, searchShopProducts);
  const [sort, setSort]                   = useState<SortKey>('default');
  const [showUnavail, setShowUnavail]     = useState(true);
  const [page, setPage]                   = useState(1);
  const [sortOpen, setSortOpen]           = useState(false);
  const [loadError, setLoadError] = useState(false);

  /* ── initial load ── */
  useEffect(() => {
    let cancelled = false;

    void Promise.allSettled([fetchAllProducts(), fetchAllCollections()])
      .then(([productsResult, collectionsResult]) => {
        if (cancelled) return;

        if (productsResult.status === 'fulfilled') {
          setAllProducts(productsResult.value);
        } else {
          setLoadError(true);
          setAllProducts([]);
        }

        if (collectionsResult.status === 'fulfilled') {
          setCollections(collectionsResult.value);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  /* ── products to show ── */
  const source = query.trim() ? displayed : allProducts;
  const nonAddonProducts = source.filter(product => !isAddonProduct(product));
  const filtered = showUnavail
    ? nonAddonProducts
    : nonAddonProducts.filter(product => !productUnavailableReason(product)
      && product.variants.nodes.some(variant => variant.availableForSale));
  const sorted   = sortProducts(filtered, sort);
  const pageData = sorted.slice(0, page * PAGE);
  const hasMore  = pageData.length < sorted.length;
  const isSearching = query.trim().length > 0;

  const clearQuery = () => { setQuery(''); setPage(1); };

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <PageBreadcrumb crumbs={[{ label: 'Sklep' }]} />

      {/* ── Page header ── */}
      <section style={{ background: '#fff', borderBottom: `1px solid ${LINE}`, padding: 'clamp(48px,6vw,72px) 0 clamp(32px,4vw,48px)' }}>
        <div className="page-container">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(20px,3vw,40px)', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.2rem,4.5vw,3.8rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.06 }}><CmsEditorialText id="editorial.SklepPage.h1.1" value={"Kwiaty i bukiety online - oferta Bukietowej"} section="Wstęp i opisy" label="Nagłówek 1" /></h1>
              {!loading && (
                <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: MUTED, margin: '8px 0 0' }}>
                  {!loadError
                    ? `${allProducts.length} ${allProducts.length === 1 ? 'produkt' : allProducts.length < 5 ? 'produkty' : 'produktów'}`
                    : 'Produkty są chwilowo niedostępne'}
                </p>
              )}
            </div>

            {/* Search */}
            <div style={{ position: 'relative', width: 'clamp(240px,35vw,380px)' }}>
              <input
                type="search"
                value={query}
                aria-label="Wyszukaj produkty w sklepie"
                onChange={e => { setQuery(e.target.value); setPage(1); }}
                placeholder="Szukaj produktów…"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  fontFamily: 'DM Sans', fontSize: 14, color: DARK,
                  background: '#fff', border: `1px solid ${LINE}`,
                  padding: '11px 40px 11px 16px', outline: 'none',
                }}
                onFocus={e => (e.currentTarget.style.borderColor = GOLD)}
                onBlur={e => (e.currentTarget.style.borderColor = LINE)}
              />
              <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                {searchLoading
                  ? <Loader2 size={15} color={GOLD} className="animate-spin" />
                  : query
                    ? <button onClick={clearQuery} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}><X size={15} color={MUTED} /></button>
                    : <Search size={15} color={MUTED} />
                }
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Shopify collections ── */}
      {!loading && collections.length > 0 && (
        <section aria-labelledby="shopify-collections-title" style={{ background: '#fff', borderBottom: `1px solid ${LINE}`, padding: '22px 0 24px' }}>
          <div className="page-container">
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 13 }}>
              <h2 id="shopify-collections-title" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: DARK, margin: 0 }}><CmsEditorialText id="editorial.SklepPage.h2.1" value={"Kolekcje kwiatów"} section="Wstęp i opisy" label="Nagłówek 1" /></h2>
              <span data-testid="text-shopify-collection-count" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: MUTED }}>
                {collections.length} {collections.length === 1 ? 'kolekcja' : collections.length < 5 ? 'kolekcje' : 'kolekcji'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'thin' }}>
              {collections.map(collection => (
                <Link
                  key={collection.id}
                  href={collectionUrl(collection.handle)}
                  data-testid={`link-shopify-collection-${collection.handle}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 170, maxWidth: 250, padding: 7, background: '#fff', border: `1px solid ${LINE}`, color: DARK, textDecoration: 'none', transition: 'border-color 0.18s, transform 0.18s' }}
                  onMouseEnter={(event: MouseEvent<HTMLAnchorElement>) => { event.currentTarget.style.borderColor = GOLD; event.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(event: MouseEvent<HTMLAnchorElement>) => { event.currentTarget.style.borderColor = LINE; event.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {collection.image ? (
                    <img
                      src={collection.image.url}
                      alt={collection.image.altText ?? ''}
                      data-testid={`img-shopify-collection-${collection.handle}`}
                      style={{ width: 38, height: 38, objectFit: 'cover', flexShrink: 0 }}
                    />
                  ) : (
                    <span aria-hidden="true" style={{ width: 38, height: 38, background: '#fff', flexShrink: 0 }} />
                  )}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, fontWeight: 600 }}>
                    {collection.title}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Toolbar ── */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${LINE}`, position: 'sticky', top: 0, zIndex: 20 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,4vw,56px)', display: 'flex', alignItems: 'center', gap: 16, height: 52, flexWrap: 'wrap' }}>
          {/* Sort dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setSortOpen(v => !v)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '7px 16px',
                border: `1px solid ${sortOpen ? DARK : 'rgba(42,31,20,0.18)'}`,
                background: sortOpen ? DARK : '#fff',
                fontFamily: 'DM Sans, sans-serif', fontSize: 10,
                letterSpacing: '0.13em', textTransform: 'uppercase',
                fontWeight: 600, color: sortOpen ? '#fff' : 'rgba(42,31,20,0.6)',
                cursor: 'pointer', transition: 'all 0.18s',
                boxShadow: sortOpen ? '0 2px 12px rgba(42,31,20,0.12)' : 'none',
              }}
            >
              <SlidersHorizontal size={13} color={sortOpen ? 'rgba(255,255,255,0.7)' : GOLD} />
              {SORT_LABELS[sort]}
              <ChevronDown size={11} color={sortOpen ? 'rgba(255,255,255,0.7)' : 'rgba(42,31,20,0.35)'} style={{ transform: sortOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>
            <AnimatePresence>
              {sortOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                  style={{ position: 'absolute', top: '100%', left: 0, background: '#fff', border: `1px solid ${LINE}`, boxShadow: '0 8px 24px rgba(0,0,0,0.08)', zIndex: 50, minWidth: 200, marginTop: 4 }}
                >
                  {(Object.keys(SORT_LABELS) as SortKey[]).map(key => (
                    <button key={key} onClick={() => { setSort(key); setSortOpen(false); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 16px', fontFamily: 'DM Sans', fontSize: 13.5, color: key === sort ? GOLD : DARK, background: key === sort ? `${GOLD}08` : 'none', border: 'none', cursor: 'pointer' }}>
                      {SORT_LABELS[key]}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div style={{ width: 1, height: 18, background: LINE, flexShrink: 0 }} />

          {/* Availability toggle */}
          <button onClick={() => { setShowUnavail(v => !v); setPage(1); }}
            style={{ display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'DM Sans', fontSize: 11.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: showUnavail ? MUTED : DARK, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
            <span style={{ width: 32, height: 17, borderRadius: 9, background: showUnavail ? LINE : GOLD, position: 'relative', display: 'inline-block', transition: 'background 0.2s', flexShrink: 0 }}>
              <span style={{ position: 'absolute', width: 13, height: 13, borderRadius: '50%', background: '#fff', top: 2, left: showUnavail ? 2 : 17, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }} />
            </span>
            Tylko dostępne
          </button>

          {query && (
            <>
              <div style={{ width: 1, height: 18, background: LINE, flexShrink: 0 }} />
              <span style={{ fontFamily: 'DM Sans', fontSize: 11.5, color: MUTED }}>
                Wyniki dla: <strong style={{ color: DARK }}>{query}</strong>
                {!searchLoading && ` (${sorted.length})`}
              </span>
              <button onClick={clearQuery} style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'DM Sans', fontSize: 11, color: GOLD, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <X size={11} /> Wyczyść
              </button>
            </>
          )}

          <div style={{ marginLeft: 'auto', fontFamily: 'DM Sans', fontSize: 11.5, color: MUTED }}>
            {!loading && !searchLoading && `${pageData.length} / ${sorted.length}`}
          </div>
        </div>
      </div>

      {/* ── Products grid ── */}
      <main style={{ maxWidth: 1280, margin: '0 auto', padding: 'clamp(32px,4vw,56px) clamp(20px,4vw,56px)' }}>
        {loading || searchLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '80px 0' }}>
            <Loader2 size={28} color={GOLD} className="animate-spin" />
            <p style={{ fontFamily: 'DM Sans', fontSize: 13.5, color: MUTED }}>Ładowanie produktów…</p>
          </div>
        ) : searchError ? (
          <div role="status" style={{ textAlign: 'center', padding: '64px 0' }}>
            <p>Nie udało się wyszukać produktów. Spróbuj ponownie za chwilę.</p>
            <button type="button" onClick={clearQuery} className="rounded-xl border px-5 py-3 mt-4">Pokaż wszystkie produkty</button>
          </div>
        ) : loadError && !isSearching ? (
          <div role="status" style={{ textAlign: 'center', padding: '64px 0' }}>
            <p>Nie udało się pobrać produktów. Spróbuj ponownie za chwilę.</p>
            <button type="button" onClick={() => window.location.reload()} className="rounded-xl border px-5 py-3 mt-4">Spróbuj ponownie</button>
          </div>
        ) : sorted.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ textAlign: 'center', padding: '80px 0' }}>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.6rem,3vw,2.4rem)', fontWeight: 400, color: DARK, margin: '0 0 12px' }}><CmsEditorialText id="editorial.SklepPage.p.1" value={"Brak wyników"} section="Wstęp i opisy" label="Akapit 1" multiline /></p>
            <p style={{ fontFamily: 'DM Sans', fontSize: 14, color: MUTED, margin: '0 0 24px', lineHeight: 1.75 }}>
              {isSearching
                ? `Nie znaleziono produktów dla „${query}".`
                : 'Brak produktów spełniających wybrane kryteria.'}
            </p>
            {isSearching && (
              <button onClick={clearQuery} style={{ fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: DARK, padding: '12px 26px', border: 'none', cursor: 'pointer' }}>
                Pokaż wszystkie
              </button>
            )}
          </motion.div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 230px), 1fr))',
              gap: 'clamp(24px,3vw,36px)',
            }}>
              {pageData.map((product, i) => (
                <ProductCard key={product.id} {...toCardProps(product, i)} />
              ))}
            </div>

            {/* Load more */}
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 'clamp(40px,5vw,64px)' }}>
                <button
                  onClick={() => setPage(p => p + 1)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'DM Sans', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, border: `1px solid rgba(42,31,20,0.22)`, background: 'none', padding: '13px 32px', cursor: 'pointer' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = GOLD; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(42,31,20,0.22)'; }}
                >
                  Pokaż więcej ({sorted.length - pageData.length} pozostałych)
                </button>
              </div>
            )}

          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
